import { API, BlockAPI, BlockTool } from '@editorjs/editorjs';
import ActivitySelectorBuilder from './builder/activity-selector-builder';
import QuizBuilder from './builder/quiz-builder';
import ActivitySelectorDomFactory from './factory/activity-selector-factory';
import QuizDomFactory from './factory/quiz-factory';
import './index.css';
import Alternative, { AlternativeRaw } from './model/alternative';
import Quiz, { QuizRaw } from './model/quiz';
import QuizService from './service/quiz-service';
import { MenuConfig } from '@editorjs/editorjs/types/tools';

export type QuizProgress = {
  answer: string;
  isCorrect: boolean;
  isDone: boolean;
};

export type AnswerCheckResult =
  | { status: 'checked'; isCorrect: boolean }
  | { status: 'unavailable' };

export interface ActivityBlockConfig {
  allowRetries: boolean;
  persist?: boolean;
  randomize?: boolean;
  callbacks: {
    onAddActivity?: () => Promise<QuizRaw>;
    onSelectActivity?: () => Promise<QuizRaw>;
    onEditActivity?: (activity: QuizRaw) => Promise<QuizRaw>;
    onCheckAnswer?: (
      alternative: AlternativeRaw,
      quiz: QuizRaw,
    ) => Promise<AnswerCheckResult | undefined>;
    onCorrectAnswer?: (alternative: AlternativeRaw) => void;
    onIncorrectAnswer?: (alternative: AlternativeRaw) => void;
    getProgress?: (quiz: QuizRaw) => Promise<QuizProgress | undefined>;
    getAnswer?: (quiz: QuizRaw) => Promise<AlternativeRaw>;
  };
}

interface ActivityBlockData {
  activity?: QuizRaw;
}

type Constructor = {
  api: API;
  data: ActivityBlockData;
  config?: ActivityBlockConfig;
  block: BlockAPI;
};

export default class ActivityBlockTool implements BlockTool {
  private readonly api: API;
  private readonly data: ActivityBlockData;
  private readonly config: ActivityBlockConfig | undefined;
  private readonly block: BlockAPI;
  private wrapper: HTMLElement | undefined;
  private quizBuilder: QuizBuilder;
  private activitySelectorBuilder: ActivitySelectorBuilder;
  private quizService: QuizService | undefined;

  static get toolbox() {
    return {
      title: 'Quiz',
      icon: `<svg viewBox="0 0 16 14" fill="none" style="scale:.75" xmlns="http://www.w3.org/2000/svg"><path d="M4.085 1.585A.563.563 0 0 0 3.29.79L1.813 2.268l-.353-.353a.563.563 0 0 0-.795.795l.75.75a.56.56 0 0 0 .795 0zm10.853 9.92H6.313l-.077.006a.563.563 0 0 0 .077 1.12h8.625l.076-.006a.562.562 0 0 0-.076-1.12m0-4.88H6.313l-.077.005a.563.563 0 0 0 .077 1.12h8.625l.076-.005a.562.562 0 0 0-.076-1.12m0-4.875H6.313l-.077.005a.563.563 0 0 0 .077 1.12h8.625l.076-.005a.562.562 0 0 0-.076-1.12M4.085 11.335a.563.563 0 1 0-.795-.795l-1.477 1.478-.353-.353a.563.563 0 0 0-.795.795l.75.75a.56.56 0 0 0 .795 0zm0-5.67a.56.56 0 0 1 0 .795L2.21 8.335a.56.56 0 0 1-.795 0l-.75-.75a.563.563 0 1 1 .795-.795l.353.353L3.29 5.665a.563.563 0 0 1 .795 0" fill="currentColor"/></svg>`,
    };
  }

  constructor({ api, data, config, block }: Constructor) {
    this.api = api;
    this.data = data;
    this.config = config;
    this.block = block;
    this.wrapper = undefined;
    this.quizBuilder = new QuizBuilder(
      new QuizDomFactory(this.api.i18n),
      !!this.config?.randomize,
    );
    this.activitySelectorBuilder = new ActivitySelectorBuilder(
      new ActivitySelectorDomFactory(this.api.i18n),
    );
    if (this.data.activity) {
      this.quizService = this.createQuizService(this.data.activity);
    }
  }

  private createQuizService(activity: QuizRaw) {
    return new QuizService(
      new Quiz(activity),
      this.block,
      new QuizDomFactory(this.api.i18n),
      this.config,
    );
  }

  onCheckAnswer = (alternative: Alternative) =>
    this.quizService?.onAnswer(alternative, this.config?.persist);

  updated(): void {
    if (!this.data.activity) return;
    this.quizService = this.createQuizService(this.data.activity);
  }
  private renderActivitySelector() {
    return this.activitySelectorBuilder
      .withCreateButton(async () => {
        const activity = await this.config?.callbacks.onAddActivity?.();
        this.data.activity = activity;
        this.rerender();
      })
      .withSelectButton(async () => {
        const activity = await this.config?.callbacks.onSelectActivity?.();
        this.data.activity = activity;
        this.rerender();
      })
      .build();
  }

  private async setupProgress(quiz: Quiz) {
    const progress = await this.config?.callbacks.getProgress?.(quiz.toJson());
    if (progress) {
      quiz.setDone(progress.isDone);
      quiz.getAlternatives().forEach(alternative => {
        if (alternative.getVerifier() === progress.answer) {
          alternative.setStatus(progress.isCorrect ? 'correct' : 'incorrect');
        }
      });
    }
    return quiz;
  }

  public async renderQuiz() {
    if (!this.data.activity) return undefined;
    let quiz = new Quiz(this.data.activity);
    quiz = await this.setupProgress(quiz);
    return this.quizBuilder
      .withTitle(quiz.getTitle())
      .withContent(quiz.getContent())
      .withAlternativesContainer()
      .withAlternatives(quiz.getAlternatives(), {
        onCheckAnswer: this.onCheckAnswer,
        disabled: this.quizService?.shouldBlockRetries(),
      })
      .build();
  }

  private clearWrapper() {
    if (this.wrapper) {
      this.wrapper.innerHTML = '';
      this.quizBuilder = new QuizBuilder(
        new QuizDomFactory(this.api.i18n),
        !!this.config?.randomize,
      );
    }
  }

  private rerender() {
    this.clearWrapper();
    this.mount();
  }

  private async mount() {
    const quiz = await this.renderQuiz();
    if (quiz) this.wrapper?.appendChild(quiz);
    else this.wrapper?.appendChild(this.renderActivitySelector());
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('activity-block-tool');
    this.mount();
    return this.wrapper;
  }

  save() {
    return {
      activity: this.data.activity,
    };
  }

  renderSettings(): HTMLElement | MenuConfig {
    return [
      {
        title: this.api.i18n.t('Reset'),
        icon: `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        `,

        onActivate: () => {
          if (!this.data.activity) return;
          const quiz = new Quiz(this.data.activity);
          quiz.reset();
          this.data.activity = quiz.toJson();
          this.rerender();
        },
      },
      {
        title: this.api.i18n.t('Edit'),
        icon: `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
        `,
        onActivate: async () => {
          if (!this.data.activity) return;
          const quiz = await this.config?.callbacks.onEditActivity?.(
            this.data.activity,
          );
          this.data.activity = quiz;
          this.rerender();
        },
      },
    ];
  }

  static get isReadOnlySupported() {
    return true;
  }

  static get pasteConfig() {
    return {
      tags: [],
    };
  }

  static get sanitize() {
    return {
      activity: {},
    };
  }
}
